import { useState } from "react";
import { DataService } from "../utils/dataService";

export type MarcaBlancaAuthParams = {
  epaycoId: string;
  typeDocument: string;
  numberDocument: string;
  documentTypeId: string | number;
};

export type EligibilityCriterion = {
  code: string;
  description: string;
  passed: boolean;
};

export type Branch = {
  id: string;
  name: string;
  formatted: string;
  countryId?: string;
  regionName?: string;
  cityName?: string;
  cityDaneCode?: string;
  addressDetail?: string;
  dataphonesAcquired?: number;
  dataphonesAvailable?: number;
};

export type MarcaBlancaAuthResponse = {
  success?: boolean;
  titleResponse?: string;
  textResponse?: string;
  documentTypeMismatch?: boolean;
  data?: {
    status: boolean;
    eligibilityCriteria?: EligibilityCriterion[];
    branches?: Branch[];
    [key: string]: any;
  };
};

type UseMarcaBlancaAuthResult = {
  authenticate: (
    params: MarcaBlancaAuthParams
  ) => Promise<MarcaBlancaAuthResponse | null>;
  authenticateByHash: (
    hash: string
  ) => Promise<MarcaBlancaAuthResponse | null>;
  loading: boolean;
};

/**
 * Autenticación del comercio comprador para la tienda de Datáfonos Marca Blanca.
 * Consulta el servicio de lookup de clientes y devuelve la respuesta completa:
 *  - data.status === true  => validación completa (el modal se cierra).
 *  - data.status === false => se muestra el modal de requisitos pendientes.
 * Ante un error de red devuelve `null`.
 */
const useMarcaBlancaAuth = (): UseMarcaBlancaAuthResult => {
  const [loading, setLoading] = useState(false);

  const authenticate = async ({
    epaycoId,
    numberDocument,
    documentTypeId,
  }: MarcaBlancaAuthParams): Promise<MarcaBlancaAuthResponse | null> => {
    setLoading(true);
    try {
      const _dataService = new DataService();
      const res = await _dataService.post("/clients/lookup", {
        source: "availabletobuymarcablanca",
        clientId: epaycoId,
        documentNumber: numberDocument,
      });
      const response = (res?.data as MarcaBlancaAuthResponse) ?? null;

      // Si el tipo de documento seleccionado no coincide con el registrado del
      // cliente, no se continúa con verify() (evita persistir una validación
      // con un tipo que sabemos que está mal) y se muestra el modal genérico.
      const responseTypeId = response?.data?.documentTypeId;
      if (
        response?.data &&
        responseTypeId !== undefined &&
        responseTypeId !== null &&
        Number(responseTypeId) !== Number(documentTypeId)
      ) {
        return { success: false, documentTypeMismatch: true };
      }

      return response;
    } catch (error: any) {
      // El backend responde HTTP 400 para "cliente no identificado" con el
      // cuerpo del envelope ({ success:false, textResponse, data:{} }). Axios
      // lanza en 4xx, así que recuperamos ese cuerpo para poder mostrar el
      // modal correspondiente. Ante un error de red (sin response) => null.
      return (error?.response?.data as MarcaBlancaAuthResponse) ?? null;
    } finally {
      setLoading(false);
    }
  };

  const authenticateByHash = async (
    hash: string
  ): Promise<MarcaBlancaAuthResponse | null> => {
    setLoading(true);
    try {
      const _dataService = new DataService();
      const res = await _dataService.post("/clients/lookup", {
        source: "availabletobuymarcablanca",
        hash,
      });
      return (res?.data as MarcaBlancaAuthResponse) ?? null;
    } catch (error: any) {
      return (error?.response?.data as MarcaBlancaAuthResponse) ?? null;
    } finally {
      setLoading(false);
    }
  };

  return { authenticate, authenticateByHash, loading };
};

export default useMarcaBlancaAuth;
